/**
 * serverEquipment（旧 REST 设备探测客户端）—— OFBiz 迁移版
 * ------------------------------------------------------------------
 * 原实现通过 $.ajax 调 /rest/equipment/* 接口（URL 拼 token 鉴权）。
 * 旧接口下线后统一返回 "token invalid."，导致前端误判登录失效。
 *
 * 处理策略（保持各方法签名不变）：
 *   A 类：有 OFBiz collector/* 新接口 → 委托 ofbiz 适配层
 *   B 类：无新接口 → 降级返回空数据
 */
import Store from '../script/store';
import Util from '../script/utils.js';
import * as ofbiz from './ofbiz';

// 降级辅助：无新接口时不调旧 /rest/，直接回调空数据
function degrade(callback, data) {
  callback && callback(data);
}

class serverEquipment {

  // 获取本机IP地址（保留原实现，不调后端）
  getUserIP(callback) {
    Util.getLocalIP((ip) => {
      Store.set('userLocalIP', ip);
    })
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => {
        if (data && data.ip) {
          Store.set('userPublicIP', data.ip);
        }
      })
      .catch(() => {});
  }

  // ==================== B 类：无新接口（降级） ====================

  // 选择登录的角色（新架构无独立 selectrole 接口，登录时已确定角色）
  SelectUserRole(role, callback, errorCallback) {
    degrade(callback, {});
  }

  // 获取资源国际化（新架构 i18n 由前端本地化资源处理）
  getEquipmentLocale(param, callback) {
    // 降级返回带 errorCode，让消费方走错误分支（不传 result）
    callback && callback({ errorCode: 1 });
  }

  // 获取监测器国际化
  getMonitorLocale(param, callback) {
    // 降级返回带 errorCode，让消费方走错误分支（不传 result）
    callback && callback({ errorCode: 1 });
  }

  // ==================== A 类：委托 OFBiz collector/* ====================

  // 资源测试连接（A 类 → collector/testConnection）
  equipmentTestConnection(data, callback, errorCallback) {
    ofbiz.testConnection({
      host: data.ServerAddress || data.host,
      equipmentId: data.equipmentId,
      community: data.community,
      port: data.port,
    }).then(
      (result) => callback && callback(result),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取资源型号（A 类 → collector/getModel）
  getResourceModal(param, callback, errorCallback) {
    ofbiz.getModel({
      host: param.ServerAddress || param.host,
      equipmentId: param.equipmentId,
      community: param.community,
      port: param.port,
    }).then(
      (result) => callback && callback(result),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取磁盘名称列表（A 类 → collector/discover?resourceType=diskspace）
  getDiskspaceNamesList(equipmentid, callback, errorCallback) {
    ofbiz.discover('diskspace', { equipmentId: equipmentid }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取磁盘IO名称列表（A 类 → collector/discover?resourceType=disksio）
  getDisksIONamesList(equipmentid, callback, errorCallback) {
    ofbiz.discover('disksio', { equipmentId: equipmentid }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取磁盘索引名称列表（A 类 → collector/discover?resourceType=disksinodes）
  getDisksNodesNamesList(equipmentid, callback, errorCallback) {
    ofbiz.discover('disksinodes', { equipmentId: equipmentid }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取硬盘列表（A 类 → collector/discover?resourceType=harddisk）
  getHardDiskNamesList(param, callback, errorCallback) {
    ofbiz.discover('harddisk', { equipmentId: param.equipmentid, monitorType: param.monitortype }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取进程名称列表（A 类 → collector/discover?resourceType=process）
  getProcessNamesList(equipmentid, callback, errorCallback) {
    ofbiz.discover('process', { equipmentId: equipmentid }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取网卡名称列表（A 类 → collector/discover?resourceType=network）
  getNetworkNamesList(param, callback, errorCallback) {
    ofbiz.discover('network', { equipmentId: param.equipmentid, monitorType: param.monitortype }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取系统服务列表（A 类 → collector/discover?resourceType=service）
  getServiceNamesList(param, callback, errorCallback) {
    ofbiz.discover('service', { equipmentId: param.equipmentid }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取脚本列表（A 类 → collector/discover?resourceType=script，后端返回空数组）
  getScriptNamesList(param, callback, errorCallback) {
    ofbiz.discover('script', { equipmentId: param.equipmentid, path: param.path }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取脚本具体内容（B 类 → 需 Agent 通道，无新接口）
  getScriptInfo(param, callback, errorCallback) {
    degrade(callback, {});
  }

  // 获取MIB文件列表（A 类 → collector/listMibFiles）
  getMibFilesList(callback, errorCallback) {
    ofbiz.listMibFiles().then(
      (data) => callback && callback({ files: Array.isArray(data) ? data : ((data && data.files) || []) }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取MIB下的计数器（A 类 → collector/listMibCounters）
  getMibCounterList(param, callback, errorCallback) {
    ofbiz.listMibCounters(param.filename).then(
      (data) => callback && callback({ values: Array.isArray(data) ? data : ((data && data.values) || []) }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取Mysql下的计数器列表（A 类 → collector/listMysqlCounters）
  getMysqlCounterList(param, callback, errorCallback) {
    ofbiz.listMysqlCounters(param.equipmentid).then(
      (data) => {
        // 旧接口 callback(result.counters)，消费方直接 dispatch(data)
        const list = Array.isArray(data) ? data : ((data && data.counters) || []);
        callback && callback(list);
      },
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取虚拟机下的列表（A 类 → collector/discoverVMware）
  getVMwareNamesList(param, callback, errorCallback) {
    ofbiz.discoverVMware({ equipmentId: param.equipmentid, monitorType: param.monitortype }).then(
      (data) => callback && callback(data || { names: [], filternames: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // ==================== B 类：报表/状态（降级） ====================

  // 获取状态报表数据（B 类 → 报表模块无对应接口）
  getStatusReportData(param, callback, errorCallback) {
    degrade(callback, []);
  }

  // 获取全部设备和监测器状态（A 类 → report/allStatusReport，按时间范围返回各监测器状态段，供状态巴图渲染）
  getAllEquipmentAndMonitor(param, callback, errorCallback) {
    ofbiz.getReportAllStatusReport({
      starttime: param && param.startTime,
      endtime: param && param.endTime,
    }).then(
      (data) => callback && callback(data || { all: [] }),
      (err) => errorCallback && errorCallback({ errorCode: 1, errmsg: (err && err.message) || '' }),
    );
  }

  // 获取安全群组（B 类 → 已由 rest_client.getGroupList → dict/securityGroups 替代）
  getSafeGroupList(callback, errorCallback) {
    degrade(callback, []);
  }

  // 刷新监测器（B 类 → 已由 deviceMonitor_action → ofbiz.refreshMonitor 替代）
  refreshMonitor(data, callback) {
    degrade(callback, []);
  }

}

const ServerEquipment = new serverEquipment();
export default ServerEquipment;
